import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import {
  blobResponseConfig,
  ensureExcelBlob,
  postMultipartFile,
} from '@/utils/blobApiUtils'
import queryString from 'query-string'
import { mapPhieuKhoFromApi } from '@/interfaces/phieuKhoCommonInterfaces'
import type { Huong, IPKCH } from '@/interfaces/phieuKhoCommonInterfaces'
import type { IPhieuNhapKhoPayload, PhieuNhapKhoId } from '@/interfaces/phieuNhapKhoInterfaces'
import { toNgayChungTuApiValue } from '@/utils/voucherTimeValidation'

const PREFIX = '/phieu-nhap-xuat-kho'

const HUONG_NHAP: Huong = 'NHAP'

function trimOpt(v: unknown): string | undefined {
  if (v == null) return undefined
  const s = String(v).trim()
  return s ? s : undefined
}

function toLinePayload(x: NonNullable<IPhieuNhapKhoPayload['danhSachHangHoa']>[number]): Record<string, unknown> {
  return {
    hangHoaId: String(x.hangHoaId),
    maHangHoa: String(x.maHangHoa || '').trim(),
    tenHangHoa: String(x.tenHangHoa || '').trim(),
    soLuong: Number(x.soLuong) || 0,
    donViTinhMua: String(x.donViTinhMua || '').trim(),
    donGia: Number(x.donGia) || 0,
    giamGia: Math.max(0, Number(x.giamGia) || 0),
    ...(trimOpt(x.hinhThucVc) ? { hinhThucVc: trimOpt(x.hinhThucVc) } : {}),
    ...(trimOpt(x.cont) ? { cont: trimOpt(x.cont) } : {}),
    ...(trimOpt(x.maVanChuyenNcc) ? { maVanChuyenNcc: trimOpt(x.maVanChuyenNcc) } : {}),
    ...(trimOpt(x.maVanChuyenAzl) ? { maVanChuyenAzl: trimOpt(x.maVanChuyenAzl) } : {}),
    ...(trimOpt(x.maYcmh) ? { maYcmh: trimOpt(x.maYcmh) } : {}),
    ...(trimOpt(x.maBaoGia) ? { maBaoGia: trimOpt(x.maBaoGia) } : {}),
    ...(trimOpt(x.ghiChu) ? { ghiChu: trimOpt(x.ghiChu) } : {}),
    ...(trimOpt(x.serialImei) ? { serialImei: trimOpt(x.serialImei) } : {}),
    ...(trimOpt(x.dateSanPham) ? { dateSanPham: trimOpt(x.dateSanPham) } : {}),
    ...(trimOpt(x.maLo) ? { maLo: trimOpt(x.maLo) } : {}),
    ...(trimOpt(x.hanSuDung) ? { hanSuDung: trimOpt(x.hanSuDung) } : {}),
  }
}

function toPhieuBody(body: IPhieuNhapKhoPayload): Record<string, unknown> {
  return {
    loaiPhieu: body.loaiPhieu,
    ...(body.maPhieu != null && String(body.maPhieu).trim() ? { maPhieu: body.maPhieu.trim() } : {}),
    ...(body.ngayChungTu != null && String(body.ngayChungTu).trim()
      ? { ngayChungTu: toNgayChungTuApiValue(String(body.ngayChungTu)) }
      : {}),
    ...(body.nhaCungCapId ? { nhaCungCapId: body.nhaCungCapId } : {}),
    ...(body.chiNhanhDoiId ? { chiNhanhDoiId: body.chiNhanhDoiId } : {}),
    ...(body.datHangNCCId ? { datHangNCCId: body.datHangNCCId } : {}),
    ...(body.phieuNhanHangId ? { phieuNhanHangId: body.phieuNhanHangId } : {}),
    ...(body.lyDo != null && String(body.lyDo).trim() ? { lyDo: body.lyDo.trim() } : {}),
    ...(body.ghiChu != null && String(body.ghiChu).trim() ? { ghiChu: body.ghiChu.trim() } : {}),
    giamGia: Math.max(0, Number(body.giamGia) || 0),
    ...(body.danhSachHangHoa && body.danhSachHangHoa.length > 0
      ? { danhSachHangHoa: body.danhSachHangHoa.map(toLinePayload) }
      : {}),
  }
}

export function phieuNhapKhoListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = { huong: HUONG_NHAP }
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  if (typeof q.search === 'string' && q.search.trim()) out.search = q.search.trim()
  if (typeof q.maHangHoa === 'string' && q.maHangHoa.trim()) out.maHangHoa = q.maHangHoa.trim()
  if (typeof q.tenHangHoa === 'string' && q.tenHangHoa.trim()) out.tenHangHoa = q.tenHangHoa.trim()
  if (typeof q.loaiPhieu === 'string' && q.loaiPhieu.trim()) out.loaiPhieu = q.loaiPhieu.trim()
  if (typeof q.trangThai === 'string' && q.trangThai.trim()) out.trangThai = q.trangThai.trim()
  if (typeof q.nhaCungCapId === 'string' && q.nhaCungCapId.trim()) {
    out.nhaCungCapId = q.nhaCungCapId.trim()
  }
  if (typeof q.chiNhanhId === 'string' && q.chiNhanhId.trim()) {
    out.chiNhanhId = q.chiNhanhId.trim()
  } else if (Array.isArray(q.chiNhanhId)) {
    const ids = q.chiNhanhId.map(String).filter(Boolean).join(',')
    if (ids) out.chiNhanhId = ids
  }
  if (typeof q.tuNgay === 'string' && q.tuNgay.trim()) out.tuNgay = q.tuNgay.trim()
  if (typeof q.denNgay === 'string' && q.denNgay.trim()) out.denNgay = q.denNgay.trim()
  if (typeof q.sort === 'string' && q.sort.trim()) out.sort = q.sort.trim()
  if (q.sortDir === 'asc' || q.sortDir === 'desc') out.sortDir = q.sortDir
  return out
}

export function fetchPhieuNhapKhoList(search: string) {
  const params = phieuNhapKhoListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phieu`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapPhieuKhoFromApi(r as Record<string, unknown>)) as IPKCH[],
        paging,
        summary,
      }
    }),
  )
}

export function fetchPhieuNhapKhoDetail(id: PhieuNhapKhoId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-phieu`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapPhieuKhoFromApi(raw) }
    }),
  )
}

export type MaPhieuKhoMoiCtx = {
  datHangNCCId?: string
  phieuNhanHangId?: string
  phieuTraId?: string
  phieuKiemKeId?: string
}

export function fetchMaPhieuNhapKhoMoi(loaiPhieu?: string, ctx?: MaPhieuKhoMoiCtx) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/ma-phieu-moi`, {
      params: {
        huong: HUONG_NHAP,
        ...(loaiPhieu ? { loaiPhieu } : {}),
        ...(ctx?.datHangNCCId ? { datHangNCCId: ctx.datHangNCCId } : {}),
        ...(ctx?.phieuNhanHangId ? { phieuNhanHangId: ctx.phieuNhanHangId } : {}),
        ...(ctx?.phieuTraId ? { phieuTraId: ctx.phieuTraId } : {}),
        ...(ctx?.phieuKiemKeId ? { phieuKiemKeId: ctx.phieuKiemKeId } : {}),
      },
    }).then((res: unknown) => {
      const r = res as { data?: unknown } | string
      if (typeof r === 'string' && r.trim()) return { ma: r.trim() }
      const d = (r as { data?: unknown }).data
      if (typeof d === 'string' && d.trim()) return { ma: d.trim() }
      if (d && typeof d === 'object' && d !== null) {
        const o = d as Record<string, unknown>
        const s = o.maPhieu ?? o.ma
        if (s != null && String(s).trim()) return { ma: String(s).trim() }
      }
      return { ma: '' }
    }),
  )
}

export function taoPhieuNhapKho(body: IPhieuNhapKhoPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-phieu`, toPhieuBody(body)))
}

export function capNhatPhieuNhapKho(id: PhieuNhapKhoId, body: IPhieuNhapKhoPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-phieu`, toPhieuBody(body)))
}

export function xoaPhieuNhapKho(id: PhieuNhapKhoId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-phieu`))
}

export function hoanThanhPhieuNhapKho(id: PhieuNhapKhoId) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/hoan-thanh-phieu`, {}))
}

export function huyPhieuNhapKho(id: PhieuNhapKhoId) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/huy-phieu`, {}))
}

export async function xuatPhieuNhapKhoTongQuanBlob(search?: string): Promise<Blob> {
  const params = search ? phieuNhapKhoListQueryFromSearch(search) : { huong: HUONG_NHAP }
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-excel-tong-quan`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function xuatPhieuNhapKhoChiTietBlob(search?: string): Promise<Blob> {
  const params = search ? phieuNhapKhoListQueryFromSearch(search) : { huong: HUONG_NHAP }
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-excel-chi-tiet`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function taiTemplatePhieuNhapKhoBlob(): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/tai-template-phieu-kho`, blobResponseConfig),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const nhapHangHoaTuExcelPhieuNhapKho = (id: string | number, file: File) =>
  apiRequest(postMultipartFile(`${PREFIX}/${id}/nhap-hang-hoa-tu-excel-phieu-kho`, file))

/** FE đã parse Excel → POST JSON `phieu-kho-nhap-danh-sach`. */
export const nhapDanhSachPhieuNhapKho = (
  rows: Array<{
    maHang: string
    soLuong: number
    donGia?: number
    giamGia?: number
    hinhThucVc?: string
    cont?: string
    maVanChuyenNcc?: string
    maVanChuyenAzl?: string
    maYcmh?: string
    maBaoGia?: string
    serialImei?: string
    dateSanPham?: string
    ghiChu?: string
  }>,
  opts?: { loaiPhieu?: string; nhaCungCapId?: string; chiNhanhDoiId?: string | number },
) => {
  const params = {
    huong: HUONG_NHAP,
    ...(opts?.loaiPhieu ? { loaiPhieu: opts.loaiPhieu } : {}),
    ...(opts?.nhaCungCapId ? { nhaCungCapId: opts.nhaCungCapId } : {}),
    ...(opts?.chiNhanhDoiId != null && String(opts.chiNhanhDoiId).trim()
      ? { chiNhanhDoiId: String(opts.chiNhanhDoiId) }
      : {}),
  }
  return apiRequest(ApisServiceApp.post(`${PREFIX}/phieu-kho-nhap-danh-sach`, rows, { params }))
}

/** @deprecated Dùng `nhapDanhSachPhieuNhapKho`. */
export const nhapPhieuNhapKhoTuExcel = (
  file: File,
  opts?: { loaiPhieu?: string },
) => {
  const qs = queryString.stringify({
    huong: HUONG_NHAP,
    ...(opts?.loaiPhieu ? { loaiPhieu: opts.loaiPhieu } : {}),
  })
  return apiRequest(postMultipartFile(`${PREFIX}/nhap-phieu-tu-excel?${qs}`, file))
}
